/**
 * GMA-1626 (CompassCare Web 11/14 — Reports hub) — pure report-request model.
 *
 * Validates/normalizes a quick-report request across type × scope × window ×
 * audience, enforcing the "family where applicable" rule: a family audience is
 * only allowed for family-safe report types and only at client scope (family
 * viewers see their own client's summaries, never agency/ops internals).
 *
 * Pure + fail-safe: malformed input yields { valid:false, errors:[...] }, never
 * a throw.
 */

export const REPORT_SCOPES = Object.freeze(["client", "group_home", "agency"]);
export const REPORT_WINDOWS = Object.freeze(["daily", "weekly", "monthly"]);
export const REPORT_AUDIENCES = Object.freeze(["clinician", "family"]);

// The standard reports. `familyAllowed` marks the client-centric summaries a
// family viewer may receive; ops/clinical-internal reports stay clinician-only.
export const REPORT_TYPES = Object.freeze({
  daily_sitrep: Object.freeze({ label: "Daily SITRep", familyAllowed: true }),
  client_summary: Object.freeze({ label: "Client Summary", familyAllowed: true }),
  group_home_summary: Object.freeze({ label: "Group Home Summary", familyAllowed: false }),
  vitals_trend: Object.freeze({ label: "Vitals Trend Analysis", familyAllowed: false }),
  alert_activity: Object.freeze({ label: "Alert Activity", familyAllowed: false }),
  device_health: Object.freeze({ label: "Device Health", familyAllowed: false }),
  client_exception: Object.freeze({ label: "Client Exception Report", familyAllowed: false }),
});

/**
 * Validate + normalize a report request.
 * @param {{type:string, scope:string, window:string, audience?:string}} config
 * @returns {{valid: boolean, request: (object|null), errors: string[]}}
 */
export function buildReportRequest(config) {
  const errors = [];
  // Distinguish absent from wrong-type (Copilot #304) so callers get
  // actionable feedback instead of "required" when they passed a string/array.
  if (config === null || config === undefined) {
    return { valid: false, request: null, errors: ["config is required"] };
  }
  if (typeof config !== "object" || Array.isArray(config)) {
    return {
      valid: false,
      request: null,
      errors: ["config must be an object"],
    };
  }

  const { type, scope, window } = config;
  // Default only when audience is genuinely absent (null/undefined). A present
  // but malformed value (e.g. "") must fall through to validation, not be
  // silently coerced to "clinician".
  const audience =
    config.audience === undefined || config.audience === null
      ? "clinician"
      : config.audience;

  // own-property check so inherited keys ("__proto__", "constructor", …) can't
  // masquerade as a valid report type and bypass catalog validation.
  // String(...) before interpolating (Copilot #304): a Symbol in a template
  // literal THROWS, which would break the module's never-throw contract.
  const show = (v) => String(v);
  const knownType = Object.prototype.hasOwnProperty.call(REPORT_TYPES, type);
  const reportType = knownType ? REPORT_TYPES[type] : null;
  if (!reportType) errors.push(`unknown report type: ${show(type)}`);
  if (!REPORT_SCOPES.includes(scope)) errors.push(`invalid scope: ${show(scope)}`);
  if (!REPORT_WINDOWS.includes(window)) errors.push(`invalid window: ${show(window)}`);
  if (!REPORT_AUDIENCES.includes(audience)) {
    errors.push(`invalid audience: ${show(audience)}`);
  }

  if (audience === "family") {
    if (reportType && !reportType.familyAllowed) {
      errors.push(`family audience not available for report type: ${show(type)}`);
    }
    if (scope !== "client") {
      errors.push("family audience is only available at client scope");
    }
  }

  if (errors.length > 0) return { valid: false, request: null, errors };
  return {
    valid: true,
    request: { type, scope, window, audience },
    errors: [],
  };
}
